/* STENCIL IMPORTS */
import {
  Component,
  Element,
  h,
  Host,
  Method,
  Prop,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
import { MercuryBundles } from "@genexus/mercury";
import { config } from "../../../common/config";
import { formatDate } from "../../../common/helpers";
import { Locale } from "../../../common/locale";
import { ContextMenuInfo } from "../../../components";
import { ItemNode } from "../../select-kb-items/select-kb-items";
import {
  renderObjectStateWithIcon,
  renderObjectTypeWithIcon
} from "../common/utilities";
import ICONS from "../version-control/common/constants";
import { ObjectState } from "../version-control/common/types";
import { mapObjectTypeToComboBoxItemModel } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/spacing",
  "utils/typography",
  "chameleon/scrollbar",
  "components/tabular-grid",
  "components/accordion",
  "components/sidebar"
];

@Component({
  tag: "gx-ide-team-dev-history",
  styleUrl: "history.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-history"]
})
export class GxIdeTeamDevHistory {
  #_componentLocale: any;

  #types: ComboBoxItemModel[] = [];

  @Element() el: HTMLGxIdeTeamDevHistoryElement;

  #fromEl?: HTMLChEditElement;
  #revisionEl?: HTMLChEditElement;
  #searchEl: HTMLChEditElement;
  #toEl?: HTMLChEditElement;
  #typesEl?: HTMLChComboBoxRenderElement;
  #userEl?: HTMLChEditElement;
  #sidebarEl: HTMLChSidebarElement;
  #revisionsGridEl: HTMLChTabularGridElement;
  #selectKBItemsEL: HTMLGxIdeSelectKbItemsElement;

  @State() historyDetail: RevisionDetailData;

  /**
   * The files rendered in the table
   */
  @State() objects: RevisionData[] = [];

  /**
   * Used to hide the select-kb-items when collapsed
   */
  @State() sidebarExpanded: boolean = true;

  /**
   * Used to show or hide the filters section
   */
  @State() filtersHidden = true;

  /**
   * To show a loader when the component is loading
   */
  @State() loading: boolean = true;

  /**
   * The Id of the selected revision in the revisions grid
   */
  @State() selectedRevisionId: string;

  /**
   * Used to customize the select-kb-items "header" part grid layout
   */
  @State() hasActiveVersion: boolean = false; // TODO:

  /**
   * Possible values of type filter
   */
  @Prop() readonly types: ObjectType[];

  /**
   * Callback emitted when the user right click on an item
   */
  @Prop() readonly itemContextMenuCallback: (
    info: ContextMenuInfo
  ) => Promise<void>;

  /**
   * Callback invoked when user right-clicks on the grid
   */
  @Prop() readonly gridContextMenuCallback: (
    info: ContextMenuInfo
  ) => Promise<void>;

  /**
   * Callback to load the items list model
   */
  @Prop() readonly loadVersionsCallback!: () => Promise<ItemNode[]>;

  /**
   * Callback invoked when is necessary to reload the history info. Receive the filters as parameters and only returns the info corresponding to the master grid.

   */
  @Prop() readonly loadCallback: LoadCallback;

  /**
   * Callback invoked to obtain detailed info from a revision. As parameter receive internal revision id
   */
  @Prop() readonly loadDetailCallback: LoadDetailCallback;

  /**
   * Callback invoked when the selected version changes. As parameter receive the selected version id
   */
  @Prop() readonly selectedVersionChangeCallback: SelectedVersionChangeCallback;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.#types = mapObjectTypeToComboBoxItemModel(this.types);
  }

  async componentDidLoad() {
    this.#sidebarEl.addEventListener(
      "expandedChange",
      (e: CustomEvent<boolean>) => {
        this.sidebarExpanded = e.detail;
      }
    );
  }

  @Method()
  async reload(): Promise<void> {
    await this.#reloadHandler();
  }

  #selectedVersionChanged = async (selectedVersion: string[]) => {
    if (this.selectedVersionChangeCallback) {
      await this.selectedVersionChangeCallback(selectedVersion);
    }
    await this.#getObjectsFromVersion(selectedVersion[0], true);
  };

  #getObjectsFromVersion = async (
    versionId: string,
    isRefresh: boolean
  ): Promise<void> => {
    this.loading = true;

    const filters: FiltersData = {
      search: this.#searchEl.value ? this.#searchEl.value : "",
      from: this.#fromEl.value ? new Date(this.#fromEl.value) : null,
      to: this.#toEl.value ? new Date(this.#toEl.value) : null,
      revision: this.#revisionEl.value ? this.#revisionEl.value : "",
      user: this.#userEl.value ? this.#userEl.value : "",
      type: this.#typesEl.value === "all" ? "" : this.#typesEl.value
    };
    try {
      this.objects = await this.loadCallback(filters, versionId, isRefresh);
      await this.#getSelectedRevision(this.objects[0]?.id);
    } catch (error) {
      this.objects = [];
      this.historyDetail = null;
      this.selectedRevisionId = null;
    } finally {
      this.loading = false;
    }
  };

  #getSelectedRevision = async (revisionId: string) => {
    if (revisionId) {
      this.historyDetail = await this.loadDetailCallback(revisionId);
      this.historyDetail.title = this.#formatHistoryDetailDate(
        this.historyDetail.title
      );
      this.selectedRevisionId = revisionId;
    } else {
      this.historyDetail = null;
    }
  };

  #registerGridContextMenuListener = (): void => {
    if (this.#revisionsGridEl) {
      this.#revisionsGridEl.addEventListener(
        "contextmenu",
        (ev: MouseEvent): void => {
          ev.preventDefault();
          ev.stopPropagation();
          this.#gridContextMenuCallbackHandler(ev);
        }
      );
    }
  };

  #gridContextMenuCallbackHandler = async (ev: MouseEvent): Promise<void> => {
    this.gridContextMenuCallback({
      selection: [this.selectedRevisionId],
      clientX: ev.clientX,
      clientY: ev.clientY
    });
  };

  #toggleFiltersHandler = () => {
    this.filtersHidden = !this.filtersHidden;
  };

  #renderCommitsGrid = (): Element => {
    const tabularGridEmpty = this.objects?.length === 0 || !this.objects;
    return (
      <ch-tabular-grid
        class={{
          "commits-grid": true,
          "tabular-grid": true,
          "tabular-grid--empty": tabularGridEmpty
        }}
        onSelectionChanged={(ev: any) => {
          this.objects.length > 0 &&
            this.#getSelectedRevision(ev.detail.rowsId[0]);
        }}
        ref={(el: HTMLChTabularGridElement) => {
          if (el && this.#revisionsGridEl !== el) {
            this.#revisionsGridEl = el as HTMLChTabularGridElement;

            this.#registerGridContextMenuListener();

            if (this.objects.length > 0) {
              this.#revisionsGridEl.selectRow(this.objects[0]?.id, true);
            }
          }
        }}
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.mainGrid.columns.id}
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size="max-content"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.mainGrid.columns.comment}
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size="1fr"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.mainGrid.columns.user}
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size="max-content"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.mainGrid.columns.commitDate}
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size="max-content"
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>
        {this.objects.length > 0 ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {this.objects.map((obj: RevisionData) => (
              <ch-tabular-grid-row
                rowid={obj.id}
                key={obj.id}
                class="tabular-grid-row"
              >
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.id}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.comment}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.user}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {`${formatDate(obj.commitDate, "date-time")}`}
                </ch-tabular-grid-cell>
              </ch-tabular-grid-row>
            ))}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              <gx-ide-empty-state
                stateTitle={this.#_componentLocale.emptyRevisionsText}
                isAnimated={true}
              ></gx-ide-empty-state>
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}

        <ch-tabular-grid-row class="tabular-grid-row"></ch-tabular-grid-row>
      </ch-tabular-grid>
    );
  };

  #renderAffectedObjectsGrid = (): Element => {
    const iterable = this.historyDetail ? this.historyDetail.objects : [];
    const tabularGridEmpty =
      this.historyDetail?.objects?.length === 0 || !this.historyDetail;
    return (
      <ch-tabular-grid
        class={{
          "affected-objects-grid": true,
          "tabular-grid": true,
          "tabular-grid--empty": tabularGridEmpty
        }}
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            column-name={
              this.#_componentLocale.affectedObjectsGrid.columns.name
            }
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size={config.tabularGrid.colSize.maxContent}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={
              this.#_componentLocale.affectedObjectsGrid.columns.types
            }
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size={config.tabularGrid.colSize.maxContent}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={
              this.#_componentLocale.affectedObjectsGrid.columns.description
            }
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size={config.tabularGrid.colSize.auto}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={
              this.#_componentLocale.affectedObjectsGrid.columns.action
            }
            column-name-position="text"
            class="tabular-grid-column"
            settingable={false}
            size={config.tabularGrid.colSize.maxContent}
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>
        {this.historyDetail && this.historyDetail.objects.length > 0 ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {iterable.map((obj: ObjectData) => (
              <ch-tabular-grid-row class="tabular-grid-row">
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.name}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {renderObjectTypeWithIcon(obj.type)}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.description}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {renderObjectStateWithIcon(obj.state)}
                </ch-tabular-grid-cell>
              </ch-tabular-grid-row>
            ))}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              <gx-ide-empty-state
                stateIconSrc={ICONS.filter}
                stateTitle={this.#_componentLocale.emptyHistoryDetailText}
                isAnimated={true}
              ></gx-ide-empty-state>
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}

        <ch-tabular-grid-row class="tabular-grid-row"></ch-tabular-grid-row>
      </ch-tabular-grid>
    );
  };

  #renderLoader = (): Element => {
    return (
      this.loading && (
        <div class="main__content scrollable">
          <gx-ide-loader
            loaderTitle={this.#_componentLocale.loader.title}
            show
            description={this.#_componentLocale.loader.description}
          ></gx-ide-loader>
        </div>
      )
    );
  };

  #formatHistoryDetailDate = (title: string): string => {
    const parts = title.split(" - ");
    const datePart = new Date(parts[1]);
    const formattedDate = formatDate(datePart, "date-time");
    return `${parts[0]} - ${formattedDate} - ${parts[2]}`;
  };

  #reloadHandler = async (): Promise<void> => {
    this.loading = true;
    await this.#selectKBItemsEL.reloadItems();
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <ch-sidebar
          ref={(el: HTMLChSidebarElement) =>
            (this.#sidebarEl = el as HTMLChSidebarElement)
          }
          class="sidebar"
          expanded={this.sidebarExpanded}
          expandButtonCollapseAccessibleName={
            this.#_componentLocale.sidebar.collapseAriaLabel
          }
          expandButtonExpandAccessibleName={
            this.#_componentLocale.sidebar.expandAriaLabel
          }
          showExpandButton
        >
          <gx-ide-select-kb-items
            class={{
              "sidebar-expanded": this.sidebarExpanded,
              "has-active-version": this.hasActiveVersion
            }}
            checkboxType="none"
            loadItemsCallback={() => this.loadVersionsCallback()}
            expandOnClick={false}
            selectionChanged={this.#selectedVersionChanged}
            itemContextMenuCallback={(info: ContextMenuInfo) =>
              this.itemContextMenuCallback(info)
            }
            ref={(el: HTMLGxIdeSelectKbItemsElement) =>
              (this.#selectKBItemsEL = el as HTMLGxIdeSelectKbItemsElement)
            }
          ></gx-ide-select-kb-items>
        </ch-sidebar>

        <main class="main spacing-body-block-start spacing-body-inline">
          <div class="main__header">
            <div class="search-filter">
              <div class="field field-inline">
                <label class="label" htmlFor="search">
                  {this.#_componentLocale.filters.search}
                </label>
                <ch-edit
                  value=""
                  class="input"
                  startImgSrc={ICONS.search}
                  part="search"
                  id="search"
                  ref={(el: HTMLChEditElement) =>
                    (this.#searchEl = el as HTMLChEditElement)
                  }
                ></ch-edit>
              </div>

              <button
                id="filter-toggle-button"
                class="button-secondary button-icon-only"
                aria-label={this.#_componentLocale.searchFilter.filterAriaLabel}
                aria-expanded={this.filtersHidden ? "false" : "true"}
                aria-controls="filters-container"
                onClick={() => this.#toggleFiltersHandler()}
              >
                <ch-image class="icon-m" src={ICONS.filter}></ch-image>
              </button>

              <button
                class="button-secondary button-icon-only"
                aria-label={this.#_componentLocale.searchFilter.reloadAriaLabel}
                onClick={() => this.#reloadHandler()}
                part={this.#_componentLocale.searchFilter.reloadAriaLabel}
              >
                <ch-image class="icon-m" src={ICONS.refresh}></ch-image>
              </button>
            </div>

            <div
              id="filters-container"
              aria-labelledby="filter-toggle-button"
              class={{
                "filters-container-visible": !this.filtersHidden,
                "filters-container": true
              }}
              inert={this.filtersHidden}
              hidden={this.filtersHidden}
            >
              <div>
                <div class="filters-grid field-group">
                  <div class="field field-block field-from">
                    <label class="label" htmlFor="from">
                      {this.#_componentLocale.filters.from}
                    </label>
                    <ch-edit
                      value=""
                      class="input"
                      type="date"
                      part="from"
                      id="from"
                      ref={(el: HTMLChEditElement) =>
                        (this.#fromEl = el as HTMLChEditElement)
                      }
                    ></ch-edit>
                  </div>

                  <div class="field field-block field-to">
                    <label class="label" htmlFor="to">
                      {this.#_componentLocale.filters.to}
                    </label>
                    <ch-edit
                      value=""
                      class="input"
                      type="date"
                      id="to"
                      ref={(el: HTMLChEditElement) =>
                        (this.#toEl = el as HTMLChEditElement)
                      }
                    ></ch-edit>
                  </div>

                  <div class="field field-block field-type">
                    <label class="label" htmlFor="type">
                      {this.#_componentLocale.filters.type}
                    </label>

                    <ch-combo-box-render
                      accessibleName="types"
                      class="combo-box"
                      part="type"
                      model={this.#types}
                      value={this.#types[0]?.value}
                      ref={(el: HTMLChComboBoxRenderElement) =>
                        (this.#typesEl = el as HTMLChComboBoxRenderElement)
                      }
                    ></ch-combo-box-render>
                  </div>

                  <div class="field field-block field-user">
                    <label class="label" htmlFor="user">
                      {this.#_componentLocale.filters.user}
                    </label>

                    <ch-edit
                      value=""
                      part="user"
                      class="input"
                      id="user"
                      ref={(el: HTMLChEditElement) =>
                        (this.#userEl = el as HTMLChEditElement)
                      }
                    ></ch-edit>
                  </div>

                  <div class="field field-block field-revision">
                    <label class="label" htmlFor="revision">
                      {this.#_componentLocale.filters.revision}
                    </label>

                    <ch-edit
                      value=""
                      part="revision"
                      class="input"
                      id="revision"
                      ref={(el: HTMLChEditElement) =>
                        (this.#revisionEl = el as HTMLChEditElement)
                      }
                    ></ch-edit>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {this.#renderLoader()}

          {!this.loading && (
            <div class="main__content scrollable">
              {this.#renderCommitsGrid()}

              <div class="field field-block">
                <label class="label" htmlFor="comment-area">
                  {this.historyDetail ? this.historyDetail.title : null}
                </label>
                <ch-edit
                  class="input comment-text-area"
                  value={this.historyDetail ? this.historyDetail.comment : null}
                  multiline
                  autoGrow
                  readonly={true}
                  part="comment-area"
                  id="comment-area"
                ></ch-edit>
              </div>

              {this.#renderAffectedObjectsGrid()}
            </div>
          )}
        </main>
      </Host>
    );
  }
}

// Types
export type ObjectType = {
  id: string;
  name: string;
  icon?: string;
};

export type FiltersData = {
  search: string;
  from?: Date;
  to?: Date;
  user?: string;
  revision?: string;
  type?: string;
};
export type RevisionData = {
  id: string;
  comment: string;
  user: string;
  commitDate: Date;
};

export type RevisionDetailData = {
  title: string;
  comment: string;
  objects: ObjectData[];
};
export type ObjectData = {
  typeIcon: string;
  state: ObjectState;
  name: string;
  type: ObjectType;
  description: string;
  action: string;
};

export type LoadCallback = (
  filters: FiltersData,
  versionId: string,
  isRefresh: boolean
) => Promise<RevisionData[]>;

export type LoadDetailCallback = (
  revision: string
) => Promise<RevisionDetailData>;

export type SelectedVersionChangeCallback = (
  selectedVersion: string[]
) => Promise<void>;
